/**
 * Grille tarifaire de la **location sans chauffeur** — un véhicule par ligne,
 * avec la dégressivité réelle et le kilométrage inclus.
 *
 * Source unique : `RENTAL_VEHICLES[].price` (app/data/vehicles.ts), le même
 * registre que celui affiché sur chaque fiche véhicule. Aucun montant en dur.
 *
 * Double bénéfice : le hub /location publie enfin ses prix (la requête
 * « location voiture cannes » et ses variantes pèsent ~2 600 impressions par
 * trimestre, et tous les loueurs locaux cachent leurs tarifs), et chaque ligne
 * est un lien interne vers la fiche du véhicule — 24 liens depuis un hub vers
 * des pages qui n'en recevaient quasiment aucun.
 *
 * Composant serveur. i18n FR/EN/AR, classes logiques (RTL-safe).
 */
import Link from "next/link";
import { RENTAL_VEHICLES } from "@/data/vehicles";
import { LOCALE_PREFIX, type Locale } from "@/config/i18n";
import { tr, type I18n } from "@/config/localeText";
import { formatEuro } from "./formatPrice";

type Dict = {
  caption: string;
  vehicle: string;
  oneToThree: string;
  fourToSix: string;
  sevenPlus: string;
  kmIncluded: string;
  perDay: string;
  note: string;
};

const UI: I18n<Dict> = {
  fr: {
    caption:
      "Tarifs de location de véhicules à la journée, par durée de location",
    vehicle: "Véhicule",
    oneToThree: "1 à 3 jours",
    fourToSix: "4 à 6 jours",
    sevenPlus: "7 jours et +",
    kmIncluded: "km/jour inclus",
    perDay: "par jour",
    note: "Tarifs par jour, dégressifs selon la durée. Le kilométrage indiqué est inclus dans le tarif journalier ; au-delà, un tarif kilométrique s'applique. Livraison possible à l'aéroport de Nice, dans les hôtels et à domicile.",
  },
  en: {
    caption: "Daily car rental rates, by rental duration",
    vehicle: "Vehicle",
    oneToThree: "1 to 3 days",
    fourToSix: "4 to 6 days",
    sevenPlus: "7 days +",
    kmIncluded: "km/day included",
    perDay: "per day",
    note: "Daily rates, decreasing with duration. The mileage shown is included in the daily rate; beyond it, a per-kilometre rate applies. Delivery available at Nice airport, to hotels and to your address.",
  },
  ar: {
    caption: "أسعار تأجير السيارات اليومية، حسب مدة التأجير",
    vehicle: "السيارة",
    oneToThree: "1 إلى 3 أيام",
    fourToSix: "4 إلى 6 أيام",
    sevenPlus: "7 أيام فأكثر",
    kmIncluded: "كم/يوم مشمولة",
    perDay: "لليوم",
    note: "أسعار يومية تنخفض كلما طالت المدة. المسافة المذكورة مشمولة في السعر اليومي؛ وبعدها يُطبَّق سعر لكل كيلومتر. التوصيل متاح إلى مطار نيس والفنادق وإلى عنوانكم.",
  },
};

export function RentalRatesTable({
  locale = "fr",
}: {
  locale?: Locale;
}): JSX.Element {
  const t = tr(UI, locale);
  const prefix = locale === "fr" ? "" : LOCALE_PREFIX[locale];

  // Du plus accessible au plus haut de gamme : l'ordre du registre suit
  // l'historique d'ajout, pas le prix.
  const rows = [...RENTAL_VEHICLES]
    .filter((v) => v.price.type === "per_day" && v.price.tiers)
    .sort((a, b) => a.price.amount - b.price.amount);

  return (
    <div>
      <div className="overflow-x-auto rounded-2xl border border-black/10 bg-white">
        <table className="w-full min-w-[42rem] border-collapse text-start text-sm">
          <caption className="sr-only">{t.caption}</caption>
          <thead>
            <tr className="border-b border-black/10 bg-black/[0.03] text-xs uppercase tracking-[0.12em] text-black/60">
              <th scope="col" className="px-4 py-3 text-start font-semibold">
                {t.vehicle}
              </th>
              <th scope="col" className="px-3 py-3 text-end font-semibold">
                {t.oneToThree}
              </th>
              <th scope="col" className="px-3 py-3 text-end font-semibold">
                {t.fourToSix}
              </th>
              <th scope="col" className="px-3 py-3 text-end font-semibold">
                {t.sevenPlus}
              </th>
              <th scope="col" className="px-4 py-3 text-end font-semibold">
                {t.kmIncluded}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((vehicle) => {
              const tiers = vehicle.price.tiers!;
              return (
                <tr
                  key={vehicle.id}
                  className="border-b border-black/5 last:border-0"
                >
                  <th scope="row" className="px-4 py-3 text-start font-medium">
                    <Link
                      href={`${prefix}/location/vehicules/${vehicle.id}`}
                      className="text-black/90 underline-offset-4 transition-colors hover:text-primary hover:underline"
                    >
                      {vehicle.name}
                    </Link>
                  </th>
                  <td className="whitespace-nowrap px-3 py-3 text-end font-semibold text-black">
                    {formatEuro(tiers.oneToThreeDays)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-end text-black/80">
                    {formatEuro(tiers.fourToSixDays)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-end text-black/80">
                    {formatEuro(tiers.sevenPlusDays)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-end text-black/60">
                    {vehicle.price.km_limit ?? "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-black/65">{t.note}</p>
    </div>
  );
}

export default RentalRatesTable;
